import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { ArrowLeft, Save, AlertTriangle, Trash2, User } from "lucide-react";

export default function ProfilePage() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [siteNotifications, setSiteNotifications] = useState(true);

  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setCompanyName(user.companyName || "");
      setEmail(user.email || "");
      setEmailNotifications(user.emailNotifications);
      setSiteNotifications(user.siteNotifications);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  const updateProfile = useMutation({
    mutationFn: async (data: {
      firstName: string | null;
      lastName: string | null;
      companyName: string | null;
      email: string | null;
      emailNotifications: boolean;
      siteNotifications: boolean;
    }) => {
      const res = await fetch("/api/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to update profile");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteAccount = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/profile/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: deleteConfirmation }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to delete account");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Account deleted", description: "Your account and all associated data have been permanently removed." });
      navigate("/");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate({
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      companyName: companyName.trim() || null,
      email: email.trim() || null,
      emailNotifications,
      siteNotifications,
    });
  };

  const handleDelete = () => {
    if (deleteConfirmation !== "delete account") return;
    deleteAccount.mutate();
  };

  if (authLoading) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-muted-foreground" role="status" aria-live="polite">Loading...</div>
        </main>
      </>
    );
  }

  if (!user) return null;

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <Button
              variant="ghost"
              className="mb-6 -ml-4 text-muted-foreground"
              onClick={() => navigate("/community")}
              data-testid="link-back-community"
            >
              <ArrowLeft size={16} className="mr-2" aria-hidden="true" /> Community Hub
            </Button>

            <div className="flex items-center gap-4 mb-10">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <User size={24} className="text-primary" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-3xl font-display font-bold" data-testid="text-profile-heading">Profile Settings</h1>
                <p className="text-muted-foreground text-sm">{user.username}</p>
              </div>
            </div>

            <form onSubmit={handleSave} aria-label="Profile settings form">
              <Card className="border-white/10 p-6 mb-8">
                <h2 className="text-lg font-display font-semibold mb-6">Personal Information</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter your first name"
                      className="bg-background border-white/10"
                      data-testid="input-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter your last name"
                      className="bg-background border-white/10"
                      data-testid="input-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter your company name"
                    className="bg-background border-white/10"
                    data-testid="input-company-name"
                  />
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="bg-background border-white/10"
                    data-testid="input-email"
                  />
                </div>
              </Card>

              <Card className="border-white/10 p-6 mb-8">
                <h2 className="text-lg font-display font-semibold mb-6">Notification Preferences</h2>
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Email Notifications</p>
                      <p className="text-sm text-muted-foreground">Receive updates, newsletters, and announcements via email</p>
                    </div>
                    <Switch
                      checked={emailNotifications}
                      onCheckedChange={setEmailNotifications}
                      aria-label="Email Notifications"
                      data-testid="switch-email-notifications"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Site Notifications</p>
                      <p className="text-sm text-muted-foreground">Receive in-app notifications for community activity</p>
                    </div>
                    <Switch
                      checked={siteNotifications}
                      onCheckedChange={setSiteNotifications}
                      aria-label="Site Notifications"
                      data-testid="switch-site-notifications"
                    />
                  </div>
                </div>
              </Card>

              <Button type="submit" disabled={updateProfile.isPending} aria-busy={updateProfile.isPending} className="w-full sm:w-auto" data-testid="button-save-profile">
                <Save size={16} className="mr-2" aria-hidden="true" />
                {updateProfile.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </form>

            <div className="mt-16 pt-8 border-t border-white/10">
              <button
                onClick={() => setShowDeleteSection(!showDeleteSection)}
                className="text-sm text-red-400 hover:text-red-300 transition-colors flex items-center gap-2"
                data-testid="button-toggle-delete"
                aria-expanded={showDeleteSection}
              >
                <Trash2 size={14} aria-hidden="true" />
                Delete Account
              </button>

              {showDeleteSection && (
                <Card className="border-red-500/30 bg-red-500/5 p-6 mt-4" role="alertdialog" aria-labelledby="delete-account-title" aria-describedby="delete-account-desc">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <h3 id="delete-account-title" className="font-display font-semibold text-red-400 mb-1">Delete Your Account</h3>
                      <p id="delete-account-desc" className="text-sm text-muted-foreground leading-relaxed">
                        This action is permanent and cannot be undone. All of your personal information, forum posts, replies, and account data will be permanently deleted in accordance with our{" "}
                        <a href="/privacy" className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer">Privacy Policy <span className="sr-only">(opens in new tab)</span></a>.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 mt-4">
                    <Label htmlFor="deleteConfirm" className="text-sm text-muted-foreground">
                      Type <span className="text-red-400 font-mono font-semibold">delete account</span> to confirm
                    </Label>
                    <Input
                      id="deleteConfirm"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder="delete account"
                      className="bg-background border-red-500/30 focus:border-red-500/60"
                      data-testid="input-delete-confirmation"
                    />
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleteConfirmation !== "delete account" || deleteAccount.isPending}
                      aria-busy={deleteAccount.isPending}
                      className="w-full"
                      data-testid="button-confirm-delete"
                    >
                      <Trash2 size={14} className="mr-2" aria-hidden="true" />
                      {deleteAccount.isPending ? "Deleting..." : "Permanently Delete Account"}
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
